import React, { Component } from "react";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";
import BookingForm from "./components/BookingForm";
import BookingsList from "./pages/BookingsList";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import HotelsPage from "./pages/HotelsPage";
import AgentsPage from "./pages/AgentsPage";
import ReportsPage from "./pages/ReportsPage";
import ConfirmPage from "./pages/ConfirmPage";
import UsersPage from "./pages/Userspage";
import PaymentsPage from "./pages/PaymentsPage";
import ActivityLogPage from "./pages/ActivityLogPage";
import VersionChecker from './components/VersionChecker';
import { api } from "./utils/api";
import {
  LayoutDashboard,
  PlusCircle,
  LayoutList,
  Hotel,
  Users,
  BarChart2,
  LogOut,
  ChevronDown,
  UserCog,
  CreditCard,
  Mail,
  KeyRound,
  X,
  History,
} from "lucide-react";

const TOAST_OPTS = {
  style: {
    background: "#fff",
    color: "#111827",
    border: "1px solid #E5E7EB",
    fontFamily: "Sora,sans-serif",
    fontSize: 13,
  },
  success: { iconTheme: { primary: "#059669", secondary: "#fff" } },
  error:   { iconTheme: { primary: "#DC2626", secondary: "#fff" } },
};

const NAV = [
  { key: "dashboard", label: "Dashboard",   Icon: LayoutDashboard, roles: null },
  { key: "list",      label: "Bookings",    Icon: LayoutList,      roles: null },
  { key: "form",      label: "New Booking", Icon: PlusCircle,      roles: null },
  { key: "hotels",    label: "Hotels",      Icon: Hotel,           roles: ["Admin", "Manager", "Staff"] },
  { key: "agents",    label: "Agents",      Icon: Users,           roles: ["Admin", "Manager", "Staff"] },
  { key: "payments",  label: "Payments",    Icon: CreditCard,      roles: ["Admin", "Manager", "Staff", "Accounts"] },
  { key: "reports",   label: "Reports",     Icon: BarChart2,       roles: ["Admin", "Manager", "Accounts"] },
  { key: "users",     label: "Users",       Icon: UserCog,         roles: ["Admin"] },
  { key: "activity",  label: "Activity Log",Icon: History,         roles: ["Admin", "Manager", "Accounts"] },
];

class App extends Component {
  constructor(props) {
    super(props);
    const savedUser  = localStorage.getItem("kts_user");
    const savedToken = localStorage.getItem("kts_token");
    this.state = {
      view: "dashboard",
      isLoggedIn: !!(savedToken && savedUser),
      user: savedUser ? JSON.parse(savedUser) : null,
      showUser: false,
      bookingDraft: null,      // { formData, step } saved when leaving mid-booking to add an agent/hotel
      newlyAddedAgent: null,   // agent just created via the "Add Agent" detour, to auto-select on return
      newlyAddedHotel: null,   // hotel just created via the "Add Hotel" detour, to auto-select on return
      showChangePassword: false,
      cpCurrent: "", cpNew: "", cpConfirm: "", cpSaving: false, cpError: "",
    };
    this.handleLogin   = this.handleLogin.bind(this);
    this.handleLogout  = this.handleLogout.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.navigate      = this.navigate.bind(this);
    this.goToAddAgentFromBooking   = this.goToAddAgentFromBooking.bind(this);
    this.handleAgentCreatedForBooking = this.handleAgentCreatedForBooking.bind(this);
    this.goToAddHotelFromBooking   = this.goToAddHotelFromBooking.bind(this);
    this.handleHotelCreatedForBooking = this.handleHotelCreatedForBooking.bind(this);
    this.handleDraftConsumed       = this.handleDraftConsumed.bind(this);
    this.handleChangePassword      = this.handleChangePassword.bind(this);
  }

  handleLogin(user)  { this.setState({ isLoggedIn: true, user, view: "dashboard" }); }
  handleLogout(silent)     {
    localStorage.removeItem("kts_token");
    localStorage.removeItem("kts_user");
    this.setState({ isLoggedIn: false, user: null, showUser: false });
    if (!silent) toast.success("Signed out");
  }

  async handleChangePassword() {
    const { cpCurrent, cpNew, cpConfirm } = this.state;
    if (!cpCurrent || !cpNew || !cpConfirm) {
      this.setState({ cpError: "All fields are required" });
      return;
    }
    if (cpNew.length < 6) {
      this.setState({ cpError: "New password must be at least 6 characters" });
      return;
    }
    if (cpNew !== cpConfirm) {
      this.setState({ cpError: "New password and confirmation don't match" });
      return;
    }
    this.setState({ cpSaving: true, cpError: "" });
    try {
      await api.post("/auth/change-password", {
        current_password: cpCurrent,
        new_password: cpNew,
      });
      // Password change invalidates every session, including this one — log
      // out immediately and send the person back to the login screen.
      this.setState({ showChangePassword: false, cpCurrent: "", cpNew: "", cpConfirm: "", cpError: "" });
      toast.success("Password changed — please log in again");
      this.handleLogout(true);
    } catch (err) {
      this.setState({ cpError: err.response?.data?.message || "Failed to change password" });
    } finally {
      this.setState({ cpSaving: false });
    }
  }
  handleSuccess()    { this.setState({ view: "list" }); }
  navigate(view)     { this.setState({ view, showUser: false }); }
  canSee(roles)      { if (!roles) return true; return roles.includes(this.state.user?.role_name); }

  // ── Add Agent mid-booking: save the in-progress booking, jump to Agents ─────
  goToAddAgentFromBooking(formData, step) {
    this.setState({ bookingDraft: { formData, step }, view: "agents" });
  }
  // Called by AgentsPage once the new agent is saved (or null if it just closed)
  handleAgentCreatedForBooking(agent) {
    this.setState({ newlyAddedAgent: agent || null, view: "form" });
  }
  // ── Add Hotel mid-booking: save the in-progress booking, jump to Hotels ────
  goToAddHotelFromBooking(formData, step) {
    this.setState({ bookingDraft: { formData, step }, view: "hotels" });
  }
  // Called by HotelsPage once the new hotel is saved (or null if it just closed)
  handleHotelCreatedForBooking(hotel) {
    this.setState({ newlyAddedHotel: hotel || null, view: "form" });
  }
  // Called by BookingForm once it has applied the restored draft / new agent / new hotel
  handleDraftConsumed() {
    this.setState({ bookingDraft: null, newlyAddedAgent: null, newlyAddedHotel: null });
  }
  getRoleColor(r)    {
    return ({ Admin: "#EF4444", Manager: "#8B5CF6", Staff: "#2563EB", Accounts: "#D97706" }[r] || "#9CA3AF");
  }

  renderPage() {
    const { view, user } = this.state;
    if (view === "dashboard") return <Dashboard   user={user} />;
    if (view === "form")      return (
      <BookingForm
        onSuccess={this.handleSuccess}
        user={user}
        resumeDraft={this.state.bookingDraft}
        newlyAddedAgent={this.state.newlyAddedAgent}
        newlyAddedHotel={this.state.newlyAddedHotel}
        onDraftConsumed={this.handleDraftConsumed}
        onRequestAddAgent={this.goToAddAgentFromBooking}
        onRequestAddHotel={this.goToAddHotelFromBooking}
      />
    );
    if (view === "hotels")    return (
      <HotelsPage
        user={user}
        autoOpenAdd={!!this.state.bookingDraft}
        onReturnToBooking={this.state.bookingDraft ? this.handleHotelCreatedForBooking : undefined}
      />
    );
    if (view === "agents")    return (
      <AgentsPage
        user={user}
        autoOpenAdd={!!this.state.bookingDraft}
        onReturnToBooking={this.state.bookingDraft ? this.handleAgentCreatedForBooking : undefined}
      />
    );
    if (view === "users")     return <UsersPage   user={user} />;
    if (view === "activity")  return <ActivityLogPage user={user} />;
    if (view === "reports")   return <ReportsPage user={user} />;
    if (view === "payments")  return <PaymentsPage user={user} />;

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: "#1E2D4F", letterSpacing: "-0.02em" }}>All Bookings</h1>
            <p style={{ fontSize: 13, color: "#6B7280", marginTop: 3 }}>Manage and track your travel bookings</p>
          </div>
          <button
            onClick={() => this.navigate("form")}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 20px", borderRadius: 12, border: "none", background: "#1E2D4F", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            <PlusCircle size={15} /> New Booking
          </button>
        </div>
        <BookingsList user={user} />
      </div>
    );
  }

  render() {
    const { isLoggedIn, user, view, showUser, showChangePassword, cpCurrent, cpNew, cpConfirm, cpSaving, cpError } = this.state;

    if (window.location.pathname.startsWith("/confirm/")) {
      return (
        <>
          <Toaster position="top-right" toastOptions={TOAST_OPTS} />
          <ConfirmPage />
        </>
      );
    }

    if (!isLoggedIn) {
      return (
        <>
          <Toaster position="top-right" toastOptions={TOAST_OPTS} />
          <LoginPage onLogin={this.handleLogin} />
        </>
      );
    }

    const nav = NAV.filter((n) => this.canSee(n.roles));

    return (
      <div style={{ minHeight: "100vh", background: "#F0F4FA", display: "flex", flexDirection: "column" }}>
        <Toaster position="top-right" toastOptions={TOAST_OPTS} />
        <VersionChecker />

        <header style={{ background: "#1E2D4F", position: "sticky", top: 0, zIndex: 50, boxShadow: "0 2px 12px rgba(0,0,0,0.15)" }}>
          <div style={{ maxWidth: 1320, margin: "0 auto", padding: "0 20px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {/* Logo */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <div style={{ width: 34, height: 34, background: "linear-gradient(135deg,#C9A84C,#a8782c)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: "#0A0F1E" }}>K</span>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>
                  KTS <span style={{ color: "#C9A84C", fontStyle: "italic" }}>Booking</span>
                </div>
                <div style={{ fontSize: 9, color: "#7FA8C9", letterSpacing: "0.12em", textTransform: "uppercase" }}>Travel CRM</div>
              </div>
            </div>

            {/* Nav */}
            <nav style={{ display: "flex", gap: 2 }}>
              {nav.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => this.navigate(key)}
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 11px", borderRadius: 8, border: "none", background: view === key ? "rgba(255,255,255,0.18)" : "transparent", fontSize: 12, fontWeight: view === key ? 700 : 500, color: view === key ? "#fff" : "rgba(255,255,255,0.65)", cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  <Icon size={13} />
                  {label}
                </button>
              ))}
            </nav>

            {/* User menu */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <button
                onClick={() => this.setState((p) => ({ showUser: !p.showUser }))}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", borderRadius: 10, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", cursor: "pointer" }}
              >
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#4A90D9,#2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff" }}>
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>{user?.first_name} {user?.last_name}</div>
                  <div style={{ fontSize: 10, color: this.getRoleColor(user?.role_name) }}>{user?.role_name}</div>
                </div>
                <ChevronDown size={12} style={{ color: "rgba(255,255,255,0.5)" }} />
              </button>

              {showUser && (
                <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 200, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", zIndex: 100 }}>
                  <div style={{ padding: "12px 14px", borderBottom: "1px solid #F3F4F6" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{user?.first_name} {user?.last_name}</div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 1 }}>{user?.email}</div>
                  </div>
                  <button
                    onClick={() => this.setState({ showChangePassword: true, showUser: false, cpCurrent: "", cpNew: "", cpConfirm: "", cpError: "" })}
                    style={{ width: "100%", padding: "11px 14px", display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#374151", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <KeyRound size={13} /> Change Password
                  </button>
                  <button
                    onClick={this.handleLogout}
                    style={{ width: "100%", padding: "11px 14px", display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#DC2626", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#FEF2F2")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <LogOut size={13} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main
          style={{ maxWidth: 1320, width: "100%", margin: "0 auto", padding: "24px 20px", flex: 1 }}
          onClick={() => showUser && this.setState({ showUser: false })}
        >
          {this.renderPage()}
        </main>

        <footer style={{ background: "#16213E", padding: "16px 24px", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#B8C8E0" }}>
            © {new Date().getFullYear()} Kabra Travel Services Pvt. Ltd. All Rights Reserved.
          </span>
          <span style={{ fontSize: 12, color: "#8E99BE", display: "flex", alignItems: "center", gap: 6, justifySelf: "center" }}>
            Designed &amp; Developed by
            <a href="https://www.versatileitsolution.com/" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center" }}>
              <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAwlJREFUeNqkld1LU2Ecx7/n2dl29uJe3ZxtSb7MppCmmZZhhQVCXgdBXXghQnQbEf0HE7rtpi68DUNK6EWKutC8EdTWi0pTbA43Z5vuxb2x7fSccbbm3KLoBx8O+z3n+e75Pb+Xw6RzOVQwG2WIMkDpojSJ/g3KEmWWMkPxlm9kygQZyihlhNKPP9s8ZYLyhMIXvYKgiIHipPD/iFPcm9cpiDHfffuPNgMRXiAYS/6VWGBxm3dPfSmI5qNlxYOOfvzsvT35fhVSCdBht+DujV5wMrZirEl/DP5Xbuw8W4HtQZ/gukdxUx4TMQEjZx0WbAWT2A4m8GbBgx+BWEWxyHoQS3emsfFwDll5DrUd9YUl4d5tRMxmf0OdFr2djUhJVUgyHD5t7h1Vo1fvebqM4HMXDla2oLtwDHKNorAqJHGIiKUBOY11+Fwj0qwS4FR49zWIeDp7SC8VSYJ54cFxmQFWkwXWa+3lfznAinWWt55mPXQGPaKpLBY8aXhDCbRa1MW3A74QPA4ZSMcJGE6aoG6pLRfsYkuKFrU1Mpx3mPDyWwRZCY+59VhRkKfhvt7nsXqrDylaHTftOkhkknLBJlL6S0IYDHcakIYEUo7D5HKkGLZ3P4VVP8Am1VAk5Giv11VMGhHbqWinbUoY5Rkk4gdY247A5Y3m/bPuMHajKfj24mjSs9ApKpbUBhF7s2g1HIvr3QYQPgc53bOwGUUqk8OHtTD8IaGs4uhpUFVrxyUiNvohu9qmh1Etha5GjsWtA0wt7sDliWEnnIJRxcBhUVYTnCXi1Jgv9baYlBiwa6GRS+ELZ+B860M0I6XJYDHo0ENTOVxBY4aII2iidEUhI7jUqoGGk4AI8ydHoGQZmFUEl1u11U4naHgLWRZG0Hjp6imrCnVaeqpEErJkBGx8D911PFrMFcMdFzVAfjcVnKWi9RoZmo1SRHYDkMZDYGIhDLZpweaPfETMWZiJxTqcdv0M0cd9yphwH0JNXrGrccbKwW7m0G5V4GKbufzOxsQ9oWoT+78/Ab8EGAAo6H72nX0jDQAAAABJRU5ErkJggg=="
                alt="Versatile" width={18} height={18} style={{ borderRadius: "50%" }} />
            </a>
          </span>
          <span />
        </footer>

        {showChangePassword && (
          <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
            onClick={() => this.setState({ showChangePassword: false })}>
            <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 400, boxShadow: "0 24px 64px rgba(0,0,0,0.22)", overflow: "hidden" }}
              onClick={(e) => e.stopPropagation()}>
              <div style={{ background: "#16213E", padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <KeyRound size={17} color="#C9A84C" />
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Change Password</span>
                </div>
                <button onClick={() => this.setState({ showChangePassword: false })}
                  style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}>
                  <X size={16} />
                </button>
              </div>
              <div style={{ padding: "22px", display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>Current Password</label>
                  <input type="password" value={cpCurrent} onChange={(e) => this.setState({ cpCurrent: e.target.value, cpError: "" })} autoFocus
                    style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 9, padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>New Password</label>
                  <input type="password" value={cpNew} onChange={(e) => this.setState({ cpNew: e.target.value, cpError: "" })}
                    style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 9, padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                  <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>At least 6 characters.</p>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>Confirm New Password</label>
                  <input type="password" value={cpConfirm} onChange={(e) => this.setState({ cpConfirm: e.target.value, cpError: "" })}
                    onKeyDown={(e) => e.key === "Enter" && this.handleChangePassword()}
                    style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 9, padding: "9px 12px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
                {cpError && <p style={{ fontSize: 12, color: "#DC2626", margin: 0 }}>{cpError}</p>}
              </div>
              <div style={{ padding: "14px 22px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => this.setState({ showChangePassword: false })}
                  style={{ padding: "9px 18px", borderRadius: 9, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Cancel</button>
                <button type="button" onClick={this.handleChangePassword} disabled={cpSaving}
                  style={{ padding: "9px 20px", borderRadius: 9, border: "none", background: cpSaving ? "#9CA3AF" : "#16213E", fontSize: 13, fontWeight: 700, color: "#fff", cursor: cpSaving ? "not-allowed" : "pointer" }}>
                  {cpSaving ? "Saving…" : "Change Password"}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes spin    { from { transform: rotate(0deg);      } to { transform: rotate(360deg);    } }
          @keyframes fadeIn  { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        `}</style>
      </div>
    );
  }
}

export default App;